import * as THREE from 'three';
import Product from '../Product';
import ProductClearanceLabel from './ProductClearanceLabel';
import { useNavigation } from '../../function/navigation';
import { UnitConversion } from '../../inputs/UnitConversion';

function SizeCheckLowerEnd({
  inputs,
  result,
  convRatio,
  drawingBaseDimensions,
}) {
  const { nav } = useNavigation();
  const targetY = convRatio * (parseFloat(inputs.h1) + result.pitch / 2);

  const reverseConfig = ['A', 'C', 'E', 'G', 'L'].includes(
    drawingBaseDimensions.configuration
  )
    ? 1
    : -1;

  let productToShow = inputs.product_tbh[nav.focusedProduct - 1];
  if (productToShow === undefined) return;
  const rectShape = productToShow.product_shape !== 'round';
  if (!rectShape) {
    productToShow.length = productToShow.diameter;
    productToShow.width = productToShow.diameter;
  }

  const inclination = THREE.MathUtils.degToRad(result.slope_angle_le);
  const inclination_db = inclination / 2;

  const heightDiffLE =
    Math.tan(inclination) * result.le_angular +
    Math.tan(inclination_db) * result.le_double_bend_value;

  const posProdz = Math.min(
    result.l1 - productToShow.length / 2,
    drawingBaseDimensions.central_diam / 2
  );

  const offsetDiff =
    (drawingBaseDimensions.offset_le_outside -
      drawingBaseDimensions.offset_le_inside) /
    2;

  const posLowerProd = posProdz + productToShow.length / 2;
  const posUpperProd = posProdz - productToShow.length / 2;
  const heightProdLower =
    heightDiffLE -
    Math.min(posLowerProd, result.le_angular) * Math.tan(inclination) -
    Math.min(
      Math.max(posLowerProd - result.le_angular, 0),
      result.le_double_bend_value
    ) *
      Math.tan(inclination_db);
  const heightProdUpper =
    heightDiffLE -
    Math.min(posUpperProd, result.le_angular) * Math.tan(inclination) -
    Math.min(
      Math.max(posUpperProd - result.le_angular, 0),
      result.le_double_bend_value
    ) *
      Math.tan(inclination_db);

  const prodInclination = Math.atan(
    (heightProdUpper - heightProdLower) / productToShow.length
  );

  const prodYpos =
    ((heightProdLower + heightProdUpper) / 2 -
      result.pitch / 2 +
      productToShow.height / 2) *
    convRatio;

  const verticalClearance =
    result.le_vertical_clearance?.[nav.focusedProduct] || 0;
  const horClearance =
    result.le_horizontal_clearance?.[nav.focusedProduct] || 0;
  const textSize = 20 / drawingBaseDimensions.nbr_windings;

  const { displayedValue: displayedHorClearance } = UnitConversion(
    horClearance,
    inputs.unit_system,
    'mm'
  );

  const { displayedValue: displayedVerClearance } = UnitConversion(
    verticalClearance,
    inputs.unit_system,
    'mm'
  );

  return (
    <group
      rotation={[0, Math.PI / 2 + (reverseConfig * Math.PI) / 2, 0]}
      position={[
        (drawingBaseDimensions.central_diam / 2) * convRatio,
        targetY,
        0,
      ]}
    >
      <group
        rotation={[prodInclination, 0, 0]}
        position={[
          -reverseConfig * (horClearance / 2 + offsetDiff) * convRatio,
          prodYpos,
          posProdz * convRatio,
        ]}
      >
        <Product
          result={result}
          convRatio={convRatio}
          product={productToShow}
          t={0}
          fixedPosition={true}
          drawingBaseDimensions={drawingBaseDimensions}
        />
      </group>
      <mesh
        rotation={[prodInclination, 0, 0]}
        position={[
          -reverseConfig * (horClearance / 2 + offsetDiff) * convRatio,
          prodYpos +
            Math.cos(prodInclination) *
              (productToShow.height / 2 + verticalClearance / 2) *
              convRatio,
          (posProdz +
            Math.sin(prodInclination) *
              (productToShow.height / 2 + verticalClearance / 2)) *
            convRatio,
        ]}
      >
        <boxGeometry
          args={[
            productToShow.width * 1.05 * convRatio,
            Math.abs(verticalClearance) * 1.05 * convRatio,
            productToShow.length * 1.05 * convRatio,
          ]}
        />
        <meshBasicMaterial
          color={verticalClearance > 0 ? 'green' : 'red'}
          transparent={true}
          opacity={0.35}
          side={THREE.DoubleSide}
        />
        <ProductClearanceLabel
          value={displayedVerClearance}
          reverseConfig={reverseConfig}
          textSize={textSize}
        />
      </mesh>

      <mesh
        rotation={[prodInclination, 0, 0]}
        position={[
          -reverseConfig * (-productToShow.width / 2 + offsetDiff) * convRatio,
          prodYpos,
          posProdz * convRatio,
        ]}
      >
        <boxGeometry
          args={[
            Math.abs(horClearance) * 1.05 * convRatio,
            productToShow.height * 1.05 * convRatio,
            productToShow.length * 1.05 * convRatio,
          ]}
        />
        <meshBasicMaterial
          color={horClearance > 0 ? 'green' : 'red'}
          transparent={true}
          opacity={0.35}
          side={THREE.DoubleSide}
        />
        <ProductClearanceLabel
          value={displayedHorClearance}
          reverseConfig={reverseConfig}
          textSize={textSize}
        />
      </mesh>
    </group>
  );
}

export default SizeCheckLowerEnd;
